/**
 * @file RepChangeRequestModal.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-30
 * @modified 2026-10-30
 * @description "Request a change" (Cam, 29 Oct 2026: "Also can we add option
 *              to amend orders or standing orders from the orders/ standing
 *              orders components for reps please, which is similar to the
 *              request form but fields can be auto-filled for the rep from the
 *              existing order/ standing order that they are requesting
 *              amendment to"). The request form in its amend mode, filled in
 *              from the order or standing order as it is now
 *              (GET /api/rep-order-requests/prefill/…); the rep edits and sends
 *              it to the office, who make the change in Edit Customer Order or
 *              the standing order window. An invoiced or archived order says
 *              why it cannot be changed (the server refuses it too).
 */

import React, { useEffect, useState } from 'react';
import Modal from './Modal';
import { showAlert } from './Alert';
import RepOrderRequestForm from './RepOrderRequestForm';
import { repRequestsApi } from '../utils/repOrderRequestApi';
import { NOT_READY_TEXT } from '../utils/repOrderRequests';
import './RepOrderRequests.css';

function RepChangeRequestModal({ kind, targetId, title, onClose, onSent }) {
  const [state, setState] = useState({ loading: true, error: null, prefill: null, additives: [], ready: true });

  useEffect(() => {
    let cancelled = false;
    const prefill = kind === 'amend_order' ? repRequestsApi.prefillOrder(targetId) : repRequestsApi.prefillStandingOrder(targetId);
    Promise.all([repRequestsApi.form(), prefill])
      .then(([form, p]) => {
        if (cancelled) return;
        setState({ loading: false, error: null, prefill: p, additives: form.additives || [], ready: form.ready !== false });
      })
      .catch(e => { if (!cancelled) setState({ loading: false, error: e.message, prefill: null, additives: [], ready: true }); });
    return () => { cancelled = true; };
  }, [kind, targetId]);

  const ref = state.prefill?.snapshot?.order_number || state.prefill?.snapshot?.reference || '';

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={title || (ref ? `Request a change — ${ref}` : 'Request a change')}
      size="md"
      className="rep-order-request-modal"
    >
      {state.loading && <div className="ror-empty">Loading…</div>}
      {state.error && <div className="ror-error ror-error-block" role="alert">{state.error}</div>}
      {!state.loading && state.prefill && state.prefill.block && (
        <div className="ror-error ror-error-block" role="alert">{state.prefill.block}</div>
      )}
      {!state.loading && state.prefill && !state.prefill.block && (
        <>
          <p className="ror-hint">
            Change what needs changing and send it — the office makes the change and the request shows
            as done on your dashboard.
          </p>
          <RepOrderRequestForm
            mode="amend"
            prefill={state.prefill}
            additives={state.additives}
            ready={state.ready}
            notReadyText={NOT_READY_TEXT}
            onSent={(request) => {
              showAlert('Sent to the office — see it under Your requests on your dashboard', 'success');
              if (onSent) onSent(request);
              onClose();
            }}
          />
        </>
      )}
    </Modal>
  );
}

export default RepChangeRequestModal;
